import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProviderChatContainer } from "../components/chat/provider/ProviderChatContainer";
import { Header } from "../components/Header";
import { Loader } from "../components/loading";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import styles from "./ChatWithProvider.module.css";

interface ProviderRelationship {
	providerId: string;
	providerName: string | null;
	providerEmail: string | null;
	providerLogo: string | null;
	businessName: string | null;
	status: string;
}

interface ChatRoom {
	id: string;
	providerUserId: string;
	patientUserId: string;
	status: string;
}

interface User {
	id: string;
	displayName: string | null;
}

export function ChatWithProvider() {
	const { logEvent } = useAnalytics();
	const { t } = useTranslation("chatWithProvider");
	const [chatRoomId, setChatRoomId] = useState<string | null>(null);
	const [hasLoggedPageView, setHasLoggedPageView] = useState(false);

	// Fetch current user
	const { data: userData, isLoading: userLoading } = useQuery<{ user: User }>({
		queryKey: ["user"],
		queryFn: async () => {
			const response = await api.get("/api/user");
			return response.data;
		},
	});

	// Fetch provider relationship
	const {
		data: providerData,
		isLoading: providerLoading,
		error: providerError,
	} = useQuery<ProviderRelationship>({
		queryKey: ["provider-relationship"],
		queryFn: async () => {
			const response = await api.get("/api/provider-chat/relationship");
			return response.data;
		},
		enabled: !!userData,
	});

	// Track page view once we know the state (connected or not)
	useEffect(() => {
		if (hasLoggedPageView || userLoading || providerLoading) return;

		const hasProvider = !providerError && !!providerData;
		logEvent("page_view", "chat_with_provider", {
			has_provider: String(hasProvider),
			provider_id: hasProvider ? providerData?.providerId : undefined,
			provider_name: hasProvider
				? providerData?.businessName || providerData?.providerName || undefined
				: undefined,
		});
		setHasLoggedPageView(true);

		// Track specific event for no provider state
		if (!hasProvider) {
			logEvent("chat_with_provider_no_provider_assigned");
		}
	}, [
		hasLoggedPageView,
		userLoading,
		providerLoading,
		providerError,
		providerData,
		logEvent,
	]);

	// Get or create chat room when provider data is available
	useEffect(() => {
		async function setupChatRoom() {
			if (!providerData || !userData) return;

			try {
				// Try to get existing chat room
				const roomsResponse = await api.get<{ rooms: ChatRoom[] }>(
					"/api/provider-chat/rooms",
				);

				if (roomsResponse.data.rooms && roomsResponse.data.rooms.length > 0) {
					// Use existing room
					setChatRoomId(roomsResponse.data.rooms[0].id);
				} else {
					// Create new chat room
					const createResponse = await api.post<{ room: ChatRoom }>(
						"/api/provider-chat/rooms",
						{
							participantId: providerData.providerId,
						},
					);
					setChatRoomId(createResponse.data.room.id);
				}
			} catch (error) {
				console.error("Error setting up chat room:", error);
			}
		}

		setupChatRoom();
	}, [providerData, userData]);

	// Loading state
	if (userLoading || providerLoading) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<div className={styles.loading}>
						<Loader size={128} />
					</div>
				</main>
			</div>
		);
	}

	// No provider assigned state
	if (providerError || !providerData) {
		return (
			<div className={styles.wrapper}>
				<Header />
				<main className={styles.main}>
					<div className={styles.emptyState}>
						<h1 className={styles.emptyTitle}>
							{t("noProviderAssigned.title")}
						</h1>
						<p className={styles.emptyDescription}>
							{t("noProviderAssigned.description")}
						</p>
						<a href="/" className={styles.emptyButton}>
							{t("noProviderAssigned.backHome")}
						</a>
					</div>
				</main>
			</div>
		);
	}

	// Chat interface
	return (
		<div className={styles.container}>
			<Header />

			{/* Main Chat Area */}
			<div className={styles.chatArea}>
				{/* Provider Info Header */}
				<div className={styles.providerHeader}>
					<div className={styles.providerInfo}>
						{/* Provider Logo */}
						<div className={styles.logoContainer}>
							{providerData.providerLogo ? (
								<img
									src={providerData.providerLogo}
									alt={
										providerData.businessName ||
										providerData.providerName ||
										"Provider"
									}
									className={styles.providerLogo}
								/>
							) : (
								<div className={styles.providerLogoFallback}>
									<span className={styles.providerInitial}>
										{(providerData.providerName || "P").charAt(0).toUpperCase()}
									</span>
								</div>
							)}
						</div>

						{/* Provider Details */}
						<div className={styles.providerDetails}>
							<h1 className={styles.providerName}>
								{providerData.businessName ||
									providerData.providerName ||
									t("provider.fallbackTitle")}
							</h1>
							<p className={styles.providerType}>
								{providerData.providerName && providerData.businessName
									? providerData.providerName
									: t("provider.type")}
							</p>
							<div className={styles.onlineStatus}>
								<div className={styles.onlineDot} />
								<span className={styles.onlineText}>
									{t("provider.online")}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Chat Container */}
				<div className={styles.chatContainer}>
					{chatRoomId && userData ? (
						<ProviderChatContainer
							chatRoomId={chatRoomId}
							participantName={
								providerData.providerName || t("provider.fallbackTitle")
							}
							currentUserId={userData.user.id}
							currentUserName={userData.user.displayName || "Patient"}
							userType="patient"
						/>
					) : (
						<div className={styles.loading}>Loading chat...</div>
					)}
				</div>
			</div>
		</div>
	);
}
